// lib/api.js
import { ApiError, ERROR_KINDS, apiFetch, ensureOk } from './apiError';
import { cache } from 'react';
// Client-side: use the public URL (e.g. /api or http://localhost:3001/api)
// Server-side: use BACKEND_URL (Docker internal) or fall back to public URL
const API_BASE_URL = typeof window !== 'undefined'
  ? (process.env.NEXT_PUBLIC_API_URL || '/api')
  : (process.env.BACKEND_URL ? `${process.env.BACKEND_URL}/api` : (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api'));

class ApiService {
  getAuthHeaders() {
    return { 'Content-Type': 'application/json' };
  }

  async login(username, password) {
    const response = await apiFetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    await ensureOk(response, 'Login failed');
    return await response.json();
  }

  async verifyToken(legacyToken = null) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/auth/verify`, {
        headers: legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {},
      });
      // 204 = geen sessie meegestuurd (anonieme bezoeker)
      if (!response.ok || response.status === 204) return { valid: false };
      return await response.json();
    } catch {
      return { valid: false, networkFailure: true };
    }
  }

  async logout() {
    const response = await apiFetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    await ensureOk(response, 'Logout failed');
    return response.json();
  }

  async fetchSets(themeFilter = null, searchQuery = null, limit = 50, offset = 0, sortBy = 'popular', signal = undefined, multibuild = false, catalog = {}) {
    const params = new URLSearchParams();
    if (themeFilter) params.append('theme', themeFilter);
    if (searchQuery) params.append('search', searchQuery);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (sortBy && sortBy !== 'popular') params.append('sort', sortBy);
    if (multibuild) params.append('multibuild', '1');
    if (catalog.labels) params.set('labels', catalog.labels);
    if (catalog.shop) params.set('shop', catalog.shop);
    const response = await apiFetch(`${API_BASE_URL}/sets?${params.toString()}`, { signal });
    await ensureOk(response, 'Failed to fetch sets');
    return await response.json();
  }

  async fetchCatalogDefinitions() {
    const response = await apiFetch(`${API_BASE_URL}/sets/label-definitions`);
    await ensureOk(response, 'Failed to fetch label definitions');
    return response.json();
  }

  async fetchCatalogSummary() {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/catalog-sync`, {headers:this.getAuthHeaders()});
    await ensureOk(response, 'Failed to fetch catalog sync status');
    return response.json();
  }

  async fetchSetHighlights() {
    const response = await apiFetch(`${API_BASE_URL}/sets/highlights`);
    await ensureOk(response, 'Failed to fetch highlights');
    return await response.json();
  }

  async fetchLegoPrice(setId, locale) {
    const response = await apiFetch(`${API_BASE_URL}/sets/${setId}/lego-price?locale=${encodeURIComponent(locale)}`);
    await ensureOk(response, 'Failed to fetch LEGO price');
    return await response.json();
  }

  async fetchPublicStats() {
    const response = await apiFetch(`${API_BASE_URL}/sets/stats`);
    await ensureOk(response, 'Failed to fetch stats');
    return await response.json();
  }

  async fetchTopThemes() {
    const response = await apiFetch(`${API_BASE_URL}/sets/themes/top`);
    await ensureOk(response, 'Failed to fetch top themes');
    return await response.json();
  }

  async fetchSetById(id, includeInvisible = false, build = null, shop = null) {
    const base = includeInvisible
      ? `${API_BASE_URL}/sets/admin/${id}`
      : `${API_BASE_URL}/sets/${id}`;
    const params = new URLSearchParams();
    if (build) params.set('build', build);
    if (shop) params.set('shop', shop);
    const endpoint = `${base}?${params}`;
    const headers = includeInvisible ? this.getAuthHeaders() : {};
    const response = await apiFetch(endpoint, { headers });
    if (response.status === 404) {
      throw new ApiError('Set not found', { kind: ERROR_KINDS.NOT_FOUND, status: 404, url: endpoint });
    }
    await ensureOk(response, 'Failed to fetch set');
    return await response.json();
  }

  async updateBuildName(setId, code, name) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/builds/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    await ensureOk(response, 'Failed to rename build');
    return await response.json();
  }

  async getBuildImageCandidates(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/build-image-candidates`, {
      headers: this.getAuthHeaders(),
    });
    await ensureOk(response, 'Failed to fetch image candidates');
    return await response.json();
  }

  async setBuildImage(setId, code, url) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/builds/${encodeURIComponent(code)}/image`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    await ensureOk(response, 'Failed to set build image');
    return await response.json();
  }

  async deleteBuild(setId, code) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/builds/${encodeURIComponent(code)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    await ensureOk(response, 'Failed to delete build');
    return await response.json();
  }

  async searchParts(setId, query, signal = undefined) {
    const response = await apiFetch(`${API_BASE_URL}/sets/${setId}/parts/search?q=${encodeURIComponent(query)}`, { signal });
    await ensureOk(response, 'Failed to search parts');
    return await response.json();
  }

  async searchPartsGlobal(query, signal = undefined) {
    const response = await apiFetch(`${API_BASE_URL}/sets/find/parts?q=${encodeURIComponent(query)}`, { signal });
    await ensureOk(response, 'Failed to search parts');
    return await response.json();
  }

  async resolvePartsGlobal(partIds, signal = undefined) {
    const response = await apiFetch(`${API_BASE_URL}/sets/find/parts/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ partIds }),
      signal,
    });
    await ensureOk(response, 'Failed to resolve element IDs');
    return await response.json();
  }

  async findBagsWithParts(parts, signal = undefined, matchMode = 'all') {
    const response = await apiFetch(`${API_BASE_URL}/sets/find/bags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parts, ...(matchMode !== 'all' && { matchMode }) }),
      signal,
    });
    await ensureOk(response, 'Failed to find bags');
    return await response.json();
  }

  async fetchAllSets({ search = null, sortBy = 'status', sortOrder = 'asc', limit = 100, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    const response = await apiFetch(`${API_BASE_URL}/sets/admin?${params.toString()}`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch all sets');
    return await response.json();
  }

  async getStatistics() {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/statistics`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch statistics');
    return await response.json();
  }

  async getAccountStats() {
    const response = await apiFetch(`${API_BASE_URL}/account/admin/stats`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch account stats');
    return await response.json();
  }

  async toggleSetVisibility(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/visibility`, { method: 'PATCH', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to toggle visibility');
    return await response.json();
  }

  async getLegoPieceCount(setId) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/lego-piece-count`, { headers: this.getAuthHeaders() });
      if (!response.ok) return { pieceCount: null };
      return await response.json();
    } catch { return { pieceCount: null }; }
  }

  async getNextHiddenSet(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/next-hidden`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to get next hidden set');
    return await response.json();
  }

  async approveSet(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/approve`, { method: 'POST', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to approve set');
    return await response.json();
  }

  async verifySet(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/verify`, { method: 'PATCH', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to verify set');
    return await response.json();
  }

  async unverifySet(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/unverify`, { method: 'PATCH', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to unverify set');
    return await response.json();
  }

  async updateSetTheme(setId, theme) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/theme`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify({ theme }) });
    await ensureOk(response, 'Failed to update theme');
    return await response.json();
  }

  async updateStickerSheets(setId, stickerSheets) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/sticker-sheets`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify({ sticker_sheets: stickerSheets }) });
    await ensureOk(response, 'Failed to update sticker sheets');
    return await response.json();
  }

  async updateAdminNote(setId, note) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/note`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify({ note }) });
    await ensureOk(response, 'Failed to update admin note');
    return await response.json();
  }

  async deleteSet(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}`, { method: 'DELETE', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to delete set');
    return await response.json();
  }

  async disconnectPartFromBag(setId, bagId, partId, colorId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}/parts/disconnect`, { method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify({ partId, colorId }) });
    await ensureOk(response, 'Failed to disconnect part');
    return await response.json();
  }

  // Verplaats een onderdeel naar een andere bag (quantity weglaten = alles)
  async movePartToBag(setId, bagId, partId, colorId, toBagId, quantity) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}/parts/move`, {
      method: 'POST', headers: this.getAuthHeaders(),
      body: JSON.stringify({ partId, colorId, toBagId, quantity }),
    });
    await ensureOk(response, 'Failed to move part');
    return await response.json();
  }

  async deletePartFromBag(setId, bagId, partId, colorId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}/parts`, { method: 'DELETE', headers: this.getAuthHeaders(), body: JSON.stringify({ partId, colorId }) });
    await ensureOk(response, 'Failed to delete part');
    return await response.json();
  }

  async deleteBag(setId, bagId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}`, { method: 'DELETE', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to delete bag');
    return await response.json();
  }

  async updatePartId(setId, bagId, oldPartId, colorId, newPartId, quantity) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}/parts`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify({ oldPartId, colorId, newPartId, quantity }) });
    await ensureOk(response, 'Failed to update part');
    return await response.json();
  }

  async updatePartQuantity(setId, bagId, partId, colorId, quantity) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}/parts/quantity`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify({ partId, colorId, quantity }) });
    await ensureOk(response, 'Failed to update quantity');
    return await response.json();
  }

  async addPartToBag(setId, bagId, partId, quantity) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}/parts/add`, { method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify({ partId, quantity }) });
    await ensureOk(response, 'Failed to add part');
    return await response.json();
  }

  async uploadPartImage(setId, bagId, imageUrl, imageFile, scale = 100) {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('imageUrl', imageUrl);
    formData.append('scale', scale.toString());
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/bags/${bagId}/parts/upload-image`, { method: 'POST', body: formData });
    await ensureOk(response, 'Failed to upload image');
    return await response.json();
  }

  async uploadBagPreviewImage(bagId, imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/bags/${bagId}/preview-image`, { method: 'POST', body: formData });
    await ensureOk(response, 'Failed to upload bag preview image');
    return await response.json();
  }

  async deleteBagPreviewImage(bagId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/bags/${bagId}/preview-image`, { method: 'DELETE' });
    await ensureOk(response, 'Failed to delete bag preview image');
    return await response.json();
  }

  async getUnassignedParts(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/unassigned-parts`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch unassigned parts');
    return await response.json();
  }

  async fetchMultiItemParts(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/multi-item-parts`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch multi-item parts');
    return await response.json();
  }

  async checkSvgParts(setId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/${setId}/check-svg-parts`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to check SVG parts');
    return await response.json();
  }

  async checkPdfExists(setId, buildCode = null) {
    try {
      const query = buildCode ? `?build=${encodeURIComponent(buildCode)}` : '';
      const response = await apiFetch(`${API_BASE_URL}/sets/${setId}/pdf/exists${query}`);
      if (!response.ok) return { exists: false };
      return await response.json();
    } catch { return { exists: false }; }
  }

  getPdfDownloadUrl(setId, buildCode = null) {
    return `${API_BASE_URL}/sets/${setId}/pdf${buildCode ? `?build=${encodeURIComponent(buildCode)}` : ''}`;
  }

  async previewRebrickableImage(elementId, scale = 20, { useLeftHalf = false } = {}) {
    const params = new URLSearchParams({ scale });
    if (useLeftHalf) params.set('useLeftHalf', 'true');
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/parts/${elementId}/preview-rebrickable?${params}`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch preview');
    return await response.json();
  }

  async replaceImageFromRebrickable(elementId, scale = 20, { useLeftHalf = false } = {}) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/parts/${elementId}/replace-image`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ scale, useLeftHalf })
    });
    await ensureOk(response, 'Failed to replace image');
    return await response.json();
  }

  async updateBagDisplayNumbers(bagId, displayNumbers) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/bags/${bagId}/display-numbers`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayNumbers })
    });
    await ensureOk(response, 'Failed to update display numbers');
    return await response.json();
  }

  async getExportImages(elementId) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/parts/${elementId}/export-images`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch export images');
    return await response.json();
  }

  getExportImageUrl(imagePath) {
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    return `${API_BASE_URL}/sets/admin/export-image/${cleanPath}`;
  }

  async useExportImage(elementId, imagePath) {
    const response = await apiFetch(`${API_BASE_URL}/sets/admin/parts/${elementId}/use-export-image`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ imagePath })
    });
    await ensureOk(response, 'Failed to use export image');
    return await response.json();
  }

  async fetchPart3D(elementId) {
    const response = await apiFetch(`${API_BASE_URL}/parts/${elementId}/3d`);
    await ensureOk(response, 'Part not found');
    return await response.json();
  }

  async requestSet(setNumber, email = null) {
    const response = await apiFetch(`${API_BASE_URL}/requests`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ setNumber, email }) });
    await ensureOk(response, 'Failed to submit request');
    return await response.json();
  }

  async getSetRequests() {
    const response = await apiFetch(`${API_BASE_URL}/requests`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch set requests');
    return await response.json();
  }

  async deleteSetRequest(requestId) {
    const response = await apiFetch(`${API_BASE_URL}/requests/${requestId}`, { method: 'DELETE', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to delete set request');
    return await response.json();
  }

  async notifySetRequest(requestId) {
    const response = await apiFetch(`${API_BASE_URL}/requests/${requestId}/notify`, { method: 'POST', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to notify set request');
    return await response.json();
  }

  async notifyVisibleSetRequests() {
    const response = await apiFetch(`${API_BASE_URL}/requests/notify-visible`, { method: 'POST', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to notify set requests');
    return await response.json();
  }

  async reportIssue(setNumber, issueType, description, email = null) {
    const response = await apiFetch(`${API_BASE_URL}/issues`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ setNumber, issueType, description, email }) });
    await ensureOk(response, 'Failed to submit issue');
    return await response.json();
  }

  async getIssues() {
    const response = await apiFetch(`${API_BASE_URL}/issues`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch issues');
    return await response.json();
  }

  async resolveIssue(issueId) {
    const response = await apiFetch(`${API_BASE_URL}/issues/${issueId}/resolve`, { method: 'POST', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to resolve issue and thank reporter');
    return await response.json();
  }

  async updateIssueStatus(issueId, status) {
    const response = await apiFetch(`${API_BASE_URL}/issues/${issueId}/status`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify({ status }) });
    await ensureOk(response, 'Failed to update issue status');
    return await response.json();
  }

  async updateIssueNotes(issueId, notes) {
    const response = await apiFetch(`${API_BASE_URL}/issues/${issueId}/notes`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify({ notes }) });
    await ensureOk(response, 'Failed to update issue notes');
    return await response.json();
  }

  async getTelemetrySummary(days = 7) {
    const response = await apiFetch(`${API_BASE_URL}/telemetry/summary?days=${days}`, { headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to fetch telemetry summary');
    return await response.json();
  }

  async deleteIssue(issueId) {
    const response = await apiFetch(`${API_BASE_URL}/issues/${issueId}`, { method: 'DELETE', headers: this.getAuthHeaders() });
    await ensureOk(response, 'Failed to delete issue');
    return await response.json();
  }
}

const api = new ApiService();
export default api;

// Server-side helper shared by page, layout and metadata. React cache voorkomt
// drie gelijktijdige API/database-reads voor één setroute; de Next fetch-cache
// bewaart het resultaat daarna nog vijf minuten tussen requests.
export const fetchSetServer = cache(async function fetchSetServer(id, shop = null) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const response = await apiFetch(`${API_BASE_URL}/sets/${id}${shop ? `?shop=${encodeURIComponent(shop)}` : ''}`, {
      next: { revalidate: 300 },
      signal: controller.signal,
    });
    if (response.status === 404) return null;
    await ensureOk(response, 'Failed to fetch set');
    return await response.json();
  } finally {
    clearTimeout(timeout);
  }
});

// Server-side helper for the homepage: fetches the initial page data in
// parallel, cached in the Next data cache and shared across all visitors
export async function fetchHomeDataServer({ theme = null, search = '', sort = 'popular', multibuild = false, labels = '', shop = '' } = {}) {
  const get = async (path, revalidate) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}${path}`, { next: { revalidate } });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  };
  const setsParams = new URLSearchParams({ limit: '50', offset: '0' });
  if (theme) setsParams.set('theme', theme);
  if (search) setsParams.set('search', search);
  if (sort && sort !== 'popular') setsParams.set('sort', sort);
  if (multibuild) setsParams.set('multibuild', '1');
  if (labels) setsParams.set('labels', labels);
  if (shop) setsParams.set('shop', shop);
  const [sets, themes, stats, highlights, catalog] = await Promise.all([
    get(`/sets?${setsParams.toString()}`, 300),
    get('/sets/themes/top', 3600),
    get('/sets/stats', 300),
    get('/sets/highlights', 300),
    get('/sets/label-definitions', 300),
  ]);
  return { sets, themes, stats, highlights, catalog };
}

// Server-side helper for public stats (about/advertise pages)
export async function fetchPublicStatsServer() {
  try {
    const response = await apiFetch(`${API_BASE_URL}/sets/stats`, { next: { revalidate: 300 } });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

// Server-side helper to fetch all visible sets (for sitemap)
export async function fetchAllSetsServer() {
  try {
    const response = await apiFetch(`${API_BASE_URL}/sets?limit=10000&offset=0`, {
      next: { revalidate: 86400 }
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.sets || [];
  } catch {
    return [];
  }
}

// Matching API
export async function fetchMatchingSets() {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets`);
  await ensureOk(response, 'Failed to fetch matching sets');
  return response.json();
}

export async function fetchNextMatch(sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/next?sessionId=${sessionId}`);
  await ensureOk(response, 'Failed to fetch next match');
  return response.json();
}

export async function submitMatchingVote(matchingSetId, stepName, elementId, sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/vote`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({
      matching_set_id: matchingSetId,
      step_name: stepName,
      element_id: elementId,
      session_id: sessionId,
    }),
  });
  await ensureOk(response, 'Failed to submit vote');
  return response.json();
}

export async function skipMatch(matchingSetId, stepName, sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/skip`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({
      matching_set_id: matchingSetId,
      step_name: stepName,
      session_id: sessionId,
    }),
  });
  await ensureOk(response, 'Failed to skip');
  return response.json();
}

export async function fetchCommunityProgress() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await apiFetch(`${API_BASE_URL}/matching/community-progress?tz=${encodeURIComponent(tz)}`);
  await ensureOk(response, 'Failed to fetch community progress');
  return response.json();
}

export async function fetchLeaderboard(sessionId, period = 'week') {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await apiFetch(`${API_BASE_URL}/matching/leaderboard?session_id=${sessionId}&period=${period}&tz=${encodeURIComponent(tz)}`);
  await ensureOk(response, 'Failed to fetch leaderboard');
  return response.json();
}

// excludeElement: bij het prefetchen van de volgende match staat de huidige nog op
// scherm; die is dan nog niet gevote en moet server-side uitgesloten worden.
export async function fetchNextGroup(sessionId, excludeElement = null) {
  const exclude = excludeElement ? `&exclude_element=${encodeURIComponent(excludeElement)}` : '';
  const response = await apiFetch(`${API_BASE_URL}/matching/next-group?sessionId=${sessionId}${exclude}`);
  await ensureOk(response, 'Failed to fetch next group');
  return response.json();
}

export async function fetchNextUnmatched(sessionId, excludeSet = null, excludeStep = null) {
  const exclude = (excludeSet && excludeStep)
    ? `&exclude_set=${excludeSet}&exclude_step=${encodeURIComponent(excludeStep)}`
    : '';
  const response = await apiFetch(`${API_BASE_URL}/matching/next-unmatched?sessionId=${sessionId}${exclude}`);
  await ensureOk(response, 'Failed to fetch next unmatched');
  return response.json();
}

function matchingAuthHeaders(contentType = true) {
  return {
    ...(contentType && { 'Content-Type': 'application/json' }),
  };
}

export async function approveGroup(matchingSetId, elementId, rejectedSteps, sessionId, isCalibration) {
  const response = await apiFetch(`${API_BASE_URL}/matching/approve-group`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({
      matching_set_id: matchingSetId,
      element_id: elementId,
      rejected_steps: rejectedSteps, // [{matching_set_id, step_name}] or ["step_name"]
      session_id: sessionId,
      is_calibration: isCalibration || false,
    }),
  });
  await ensureOk(response, 'Failed to approve group');
  return response.json();
}

// "Can't tell / image cut off" voor step-crops; steps = [{matching_set_id, step_name}]
export async function reportUnclearSteps(steps, sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/report-unclear`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ steps, session_id: sessionId }),
  });
  await ensureOk(response, 'Failed to report image');
  return response.json();
}

export async function fetchMatchingSet(setId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${setId}`);
  await ensureOk(response, 'Failed to fetch matching set');
  return response.json();
}

export async function approveBulk(matchingSetId, stepNames, sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/approve-bulk`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({
      matching_set_id: matchingSetId,
      step_names: stepNames,
      session_id: sessionId,
    }),
  });
  await ensureOk(response, 'Failed to bulk approve');
  return response.json();
}

export async function matchUnmatchedBulk(matchingSetId, matches) {
  const response = await apiFetch(`${API_BASE_URL}/matching/match-unmatched-bulk`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({
      matching_set_id: matchingSetId,
      matches,
    }),
  });
  await ensureOk(response, 'Failed to match unmatched steps');
  return response.json();
}

export async function approveMatchingSet(matchingSetId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${matchingSetId}/approve-set`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
  });
  await ensureOk(response, 'Failed to approve set');
  return response.json();
}

export async function setMatchingSetStatus(matchingSetId, status) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${matchingSetId}/set-status`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ status }),
  });
  await ensureOk(response, 'Failed to set status');
  return response.json();
}

export async function deleteMatchingSet(matchingSetId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${matchingSetId}`, {
    method: 'DELETE',
    headers: matchingAuthHeaders(),
  });
  await ensureOk(response, 'Failed to delete matching set');
  return response.json();
}

export async function updateBagPages(matchingSetId, bagPages) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${matchingSetId}/bag-pages`, {
    method: 'PUT',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ bag_pages: bagPages }),
  });
  await ensureOk(response, 'Failed to update bag pages');
  return response.json();
}

// Multi-build (3-in-1) bijwerken: paginabereiken per boekje, buildnamen en de
// handmatige groepsindeling per alternatief model.
export async function updateMatchingBuilds(matchingSetId, builds) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${matchingSetId}/builds`, {
    method: 'PUT',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ builds }),
  });
  await ensureOk(response, 'Failed to update builds');
  return response.json();
}

export async function updateStepQuantity(matchingSetId, stepName, quantity) {
  const response = await apiFetch(`${API_BASE_URL}/matching/step-quantity`, {
    method: 'PUT',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ matching_set_id: matchingSetId, step_name: stepName, quantity }),
  });
  await ensureOk(response, 'Failed to update step quantity');
  return response.json();
}

export async function rejectStep(matchingSetId, stepName, sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/reject-step`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ matching_set_id: matchingSetId, step_name: stepName, session_id: sessionId }),
  });
  await ensureOk(response, 'Failed to reject step');
  return response.json();
}

export async function deleteMatchingStep(matchingSetId, stepName) {
  const response = await apiFetch(`${API_BASE_URL}/matching/step/${matchingSetId}/${encodeURIComponent(stepName)}`, {
    method: 'DELETE',
    headers: matchingAuthHeaders(false),
  });
  await ensureOk(response, 'Failed to delete step');
  return response.json();
}

export async function fetchMatchingInventory(matchingSetId, stepName) {
  const qs = stepName ? `?step_name=${encodeURIComponent(stepName)}` : '';
  const response = await apiFetch(`${API_BASE_URL}/matching/inventory/${matchingSetId}${qs}`);
  await ensureOk(response, 'Failed to fetch inventory');
  // Zonder step_name: kale array. Met step_name: { inventory, sort_hints }.
  return response.json();
}

export async function reassignStep(matchingSetId, stepName, newElementId, sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/reassign-step`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ matching_set_id: matchingSetId, step_name: stepName, new_element_id: newElementId, session_id: sessionId }),
  });
  await ensureOk(response, 'Failed to reassign step');
  return response.json();
}

export async function undoVotes(matchingSetId, actionType, stepNames, sessionId, elementId, originalAiMatches, steps) {
  const response = await apiFetch(`${API_BASE_URL}/matching/undo-votes`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({
      matching_set_id: matchingSetId,
      action_type: actionType,
      step_names: stepNames,
      steps: steps, // [{matching_set_id, step_name}] for cross-set
      session_id: sessionId,
      element_id: elementId,
      original_ai_matches: originalAiMatches,
    }),
  });
  await ensureOk(response, 'Failed to undo');
  return response.json();
}

export async function flagInventory(matchingSetId, elementId, sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/flag-inventory`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ matching_set_id: matchingSetId, element_id: elementId, session_id: sessionId }),
  });
  await ensureOk(response, 'Failed to flag inventory');
  return response.json();
}

export async function unflagInventory(matchingSetId, elementId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/unflag-inventory`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ matching_set_id: matchingSetId, element_id: elementId }),
  });
  await ensureOk(response, 'Failed to unflag inventory');
  return response.json();
}

export async function addInventoryItem(matchingSetId, elementId, quantity) {
  const response = await apiFetch(`${API_BASE_URL}/matching/inventory/${matchingSetId}`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ element_id: elementId, quantity }),
  });
  await ensureOk(response, 'Failed to add inventory item');
  return response.json();
}

export async function addMatchingSteps(matchingSetId, steps) {
  // steps: [{ element_id, quantity, page_number, side }]
  const response = await apiFetch(`${API_BASE_URL}/matching/step/${matchingSetId}`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ steps }),
  });
  await ensureOk(response, 'Failed to add steps');
  return response.json();
}

export async function deleteInventoryItem(matchingSetId, elementId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/inventory/${matchingSetId}/${elementId}`, {
    method: 'DELETE',
    headers: matchingAuthHeaders(false),
  });
  await ensureOk(response, 'Failed to delete inventory item');
  return response.json();
}

export async function updateInventoryQuantity(matchingSetId, elementId, quantity) {
  const response = await apiFetch(`${API_BASE_URL}/matching/inventory/${matchingSetId}/${elementId}/quantity`, {
    method: 'PUT',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ quantity }),
  });
  await ensureOk(response, 'Failed to update quantity');
  return response.json();
}

export async function fetchMatchingGrouped(setId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${setId}/grouped`);
  await ensureOk(response, 'Failed to fetch grouped matches');
  return response.json();
}

export async function fetchCommunityVotes(matchingSetId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/sets/${matchingSetId}/community-votes`);
  await ensureOk(response, 'Failed to fetch community votes');
  return response.json();
}

export async function fetchMultiplierRanges(matchingSetId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/multiplier-ranges/${matchingSetId}`);
  await ensureOk(response, 'Failed to fetch multiplier ranges');
  return response.json();
}

export async function saveMultiplierRange(matchingSetId, range) {
  const response = await apiFetch(`${API_BASE_URL}/matching/multiplier-ranges/${matchingSetId}`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify(range),
  });
  await ensureOk(response, 'Failed to save multiplier range');
  return response.json();
}

export async function deleteMultiplierRange(matchingSetId, rangeId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/multiplier-ranges/${matchingSetId}/${rangeId}`, {
    method: 'DELETE',
    headers: matchingAuthHeaders(false),
  });
  await ensureOk(response, 'Failed to delete multiplier range');
  return response.json();
}

export async function detectMissingMultipliers(matchingSetId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/detect-missing-multipliers/${matchingSetId}`);
  await ensureOk(response, 'Failed to detect missing multipliers');
  return response.json();
}

// Hybrid matcher stats
export async function fetchLabelAudits(status = 'pending') {
  const response = await apiFetch(`${API_BASE_URL}/matching/audit?status=${encodeURIComponent(status)}`, {
    headers: matchingAuthHeaders(false),
  });
  await ensureOk(response, 'Failed to fetch label audits');
  return response.json();
}

export async function resolveLabelAudit(auditId, action) {
  const response = await apiFetch(`${API_BASE_URL}/matching/audit/${auditId}/resolve`, {
    method: 'POST',
    headers: matchingAuthHeaders(),
    body: JSON.stringify({ action }),
  });
  await ensureOk(response, 'Failed to resolve label audit');
  return response.json();
}

export async function fetchHybridStats() {
  const response = await apiFetch(`${API_BASE_URL}/matching/hybrid-stats`, {
    headers: matchingAuthHeaders(false),
  });
  await ensureOk(response, 'Failed to fetch hybrid stats');
  return response.json();
}

export async function calculateHybridAccuracy(matchingSetId) {
  const response = await apiFetch(`${API_BASE_URL}/matching/hybrid-stats/${matchingSetId}/calculate`, {
    method: 'POST',
    headers: matchingAuthHeaders(false),
  });
  await ensureOk(response, 'Failed to calculate hybrid accuracy');
  return response.json();
}
